const ACCESS_TOKEN_KEY = 'pm_access_token';
const REFRESH_TOKEN_KEY = 'pm_refresh_token';
const EMPLOYER_ID_KEY = 'pm_employer_id';
const AUTH_CHANNEL_NAME = 'pm-auth-sync';

export type AuthSyncMessage =
  | { type: 'logout' }
  | { type: 'session-changed'; employerId: number | null };

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getStoredEmployerId(): number | null {
  const raw = localStorage.getItem(EMPLOYER_ID_KEY);
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

/** Decode employerId from a JWT access token payload (no signature verification). */
export function getEmployerIdFromAccessToken(token?: string | null): number | null {
  const accessToken = token === undefined ? getAccessToken() : token;
  if (!accessToken) return null;
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const json = atob(base64);
    const payload = JSON.parse(json) as { employerId?: number | string };
    if (payload.employerId == null || payload.employerId === '') return null;
    const id = Number(payload.employerId);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function persistEmployerIdFromToken(accessToken: string): void {
  const employerId = getEmployerIdFromAccessToken(accessToken);
  if (employerId != null) {
    localStorage.setItem(EMPLOYER_ID_KEY, String(employerId));
  } else {
    localStorage.removeItem(EMPLOYER_ID_KEY);
  }
}

function broadcastAuthSync(message: AuthSyncMessage): void {
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
  } catch {
    // BroadcastChannel unsupported — storage events still cover other tabs
  }
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  persistEmployerIdFromToken(accessToken);
  broadcastAuthSync({
    type: 'session-changed',
    employerId: getEmployerIdFromAccessToken(accessToken),
  });
}

export function setAccessToken(accessToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  persistEmployerIdFromToken(accessToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EMPLOYER_ID_KEY);
  broadcastAuthSync({ type: 'logout' });
}

/** Subscribe to cross-tab auth/company changes. Returns an unsubscribe function. */
export function subscribeAuthSync(handler: (message: AuthSyncMessage) => void): () => void {
  let channel: BroadcastChannel | null = null;

  try {
    channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<AuthSyncMessage>) => {
      if (event.data?.type) handler(event.data);
    };
  } catch {
    channel = null;
  }

  const onStorage = (event: StorageEvent) => {
    if (
      event.key === ACCESS_TOKEN_KEY ||
      event.key === REFRESH_TOKEN_KEY ||
      event.key === EMPLOYER_ID_KEY ||
      event.key === null
    ) {
      const token = getAccessToken();
      if (!token) {
        handler({ type: 'logout' });
      } else {
        handler({ type: 'session-changed', employerId: getEmployerIdFromAccessToken(token) });
      }
    }
  };

  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener('storage', onStorage);
    channel?.close();
  };
}

export const authStorageKeys = {
  accessToken: ACCESS_TOKEN_KEY,
  refreshToken: REFRESH_TOKEN_KEY,
  employerId: EMPLOYER_ID_KEY,
} as const;
